import { useCallback, useEffect, useRef, useState } from 'react'
import type { Plan } from '../plan'
import {
  addComparison,
  removeComparison,
  setActive,
  updateActivePlan,
} from '../scenarios'
import type { ScenarioId, ScenarioSet } from '../scenarios'
import { encodeScenarios, readScenariosFromLocation } from '../utils/planCodec'

/**
 * Holds the scenario set, and keeps the URL in step with it.
 *
 * The address bar is the save file. No account, no server-side plan storage and
 * no localStorage for plan data — a link is the only persistence this product has,
 * and it is enough: it survives being pasted into WhatsApp, it reproduces the
 * sender's chart exactly, and it never becomes something that has to be deleted
 * on request.
 */
export interface ScenarioControls {
  set: ScenarioSet
  /** Patches the active scenario. `currentAge` propagates to both — see `scenarios.ts`. */
  updatePlan: (patch: Partial<Plan>) => void
  /** Adds the second scenario as a copy of the active one, and focuses it. */
  compare: () => void
  /** Drops the comparison, keeping whichever scenario is focused. */
  stopComparing: () => void
  focus: (id: ScenarioId) => void
}

export function useScenarioState(): ScenarioControls {
  const [set, setSet] = useState<ScenarioSet>(() => readScenariosFromLocation())
  const writingRef = useRef(false)

  // Mirror the set into the hash. `replaceState`, not `pushState`: dragging a
  // slider would otherwise stack hundreds of history entries and turn the back
  // button into a tarpit the user cannot escape.
  useEffect(() => {
    const hash = encodeScenarios(set)
    if (hash === window.location.hash) return

    writingRef.current = true
    window.history.replaceState(null, '', hash)
    // Cleared on a microtask rather than synchronously — hashchange, when it
    // fires at all, arrives after the current task.
    queueMicrotask(() => {
      writingRef.current = false
    })
  }, [set])

  // Adopt a hash the user pasted in or arrived at via back/forward, but ignore
  // the events our own writes cause.
  useEffect(() => {
    const onHashChange = () => {
      if (writingRef.current) return
      setSet(readScenariosFromLocation())
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const updatePlan = useCallback((patch: Partial<Plan>) => {
    setSet((previous) => updateActivePlan(previous, patch))
  }, [])

  const compare = useCallback(() => setSet(addComparison), [])
  const stopComparing = useCallback(() => setSet(removeComparison), [])
  const focus = useCallback((id: ScenarioId) => {
    setSet((previous) => setActive(previous, id))
  }, [])

  return { set, updatePlan, compare, stopComparing, focus }
}
